import { t } from '@lingui/core/macro'

import type { WorkOrderEntry } from './workOrder'
import { workOrderDate } from './workOrderDates'
import { workOrderEvidence } from './workOrderEvidence'
import { workOrderAdditionalFields } from './workOrderFields'
import { normalizeWorkOrderText, workOrderPlainText } from './workOrderText'

export interface WorkOrderField {
  id: string
  label: string
  value: string
}

export function workOrderPresentation(entry: WorkOrderEntry) {
  const { record: wo } = entry.data
  const additional = workOrderAdditionalFields(entry)
  const title = workOrderPlainText(wo.description) || t`Work order`
  const status = wo.status_description
    ? `${wo.status_description}${wo.status ? ` (${wo.status})` : ''}`
    : wo.status
  const field = (id: string, label: string, value: string): WorkOrderField => ({
    id,
    label,
    value,
  })
  const context = [
    field(
      'task',
      t`Record kind`,
      wo.istask === true
        ? t`Task (istask=true)`
        : wo.istask === false
          ? t`Work order (istask=false)`
          : ''
    ),
    field('asset', t`Asset`, wo.assetnum),
    field('location', t`Location`, wo.location),
  ].filter((item) => item.value)
  const metadata = [
    field(
      'type',
      t`Work type`,
      [wo.worktype, wo.woclass_description || wo.woclass]
        .filter(Boolean)
        .join(' · ')
    ),
    field(
      'priority',
      t`Priority`,
      [wo.wopriority, wo.wopriority_description].filter(Boolean).join(' · ')
    ),
    field(
      'reported',
      t`Reported`,
      [workOrderDate(wo.reportdate), wo.reportedby].filter(Boolean).join(' · ')
    ),
    field('statusDate', t`Status updated`, workOrderDate(wo.statusdate)),
    field('pm', t`Preventive maintenance`, wo.pmnum),
    field('parent', t`Parent work order`, wo.parent),
    field(
      'group',
      t`WO group (wogroup)`,
      wo.wogroup !== wo.wonum ? wo.wogroup : ''
    ),
    field('jobPlan', t`Job plan (jpnum)`, wo.jpnum),
  ].filter((item) => item.value)
  const schedule = [
    field('targetStart', t`Target start`, workOrderDate(wo.targstartdate)),
    field('targetFinish', t`Target finish`, workOrderDate(wo.targcompdate)),
    field('actualStart', t`Actual start`, workOrderDate(wo.actstart)),
    field('actualFinish', t`Actual finish`, workOrderDate(wo.actfinish)),
  ].filter((item) => item.value)
  const description = workOrderPlainText(wo.description_longdescription)
  const logs = wo.worklog
    .map((log, index) => {
      const short = workOrderPlainText(log.description)
      const detail = workOrderPlainText(log.description_longdescription)
      return {
        id: `worklog.${index}`,
        date: log.createdate,
        heading: [workOrderDate(log.createdate), log.createby, log.logtype]
          .filter(Boolean)
          .join(' · '),
        title: short,
        body:
          normalizeWorkOrderText(short) === normalizeWorkOrderText(detail)
            ? ''
            : detail,
      }
    })
    .filter((log) => log.title || log.body)
    .sort((a, b) => a.date.localeCompare(b.date))
  return {
    title,
    status,
    context,
    metadata,
    schedule,
    additional,
    logs,
    description:
      normalizeWorkOrderText(description) === normalizeWorkOrderText(title)
        ? ''
        : description,
    evidence: workOrderEvidence(entry),
  }
}
